import { Box, styled, IconButton } from '@mui/material'
import {
	colors,
	shadows,
	spacing,
	typography,
	sizing,
	radius,
	focusVisible,
} from '@/styles/designTokens'
import { Card as BaseCard } from '@/components/common/ui/Modal/Modal.styled'
export { DrawerPuller } from '@/components/common/ui/DrawerPuller.styled'

// SwipeableDrawer Paper sx — mobile bottom sheet for the sign-in modal (≤719px only).
// Do NOT set position — MUI needs position:fixed on the Paper to anchor to the viewport bottom.
export const signInDrawerPaperSx = {
	backgroundColor: colors.surfaceWhite,
	borderRadius: `${radius.sheet}px ${radius.sheet}px 0 0`,
	width: '100%',
	maxWidth: 480,
	maxHeight: '90vh',
	overflow: 'hidden',
	display: 'flex',
	flexDirection: 'column' as const,
	boxShadow: shadows.modal,
	margin: '0 auto',
} as const

export const StyledModalCard = styled(BaseCard, {
	shouldForwardProp: (prop) => prop !== 'isEdit' && prop !== 'width',
})<{ isEdit: boolean; width: number }>(() => ({
	display: 'flex',
	flexDirection: 'column',
}))

export const StyledContentBox = styled(Box)({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	gap: `${spacing.lg}px`,
	padding: `${spacing.xl}px`,
	alignSelf: 'stretch',
	overflow: 'auto',
	flex: 1,
	minHeight: sizing.contentBoxMinHeight,
	justifyContent: 'flex-start',
	backgroundColor: colors.surfaceMuted,
})

export const StyledPhoneInputContainer = styled(Box)(({ theme }) => ({
	display: 'flex',
	width: '100%',
	alignItems: 'stretch',

	'& .PhoneInput': {
		display: 'flex',
		width: '100%',
		alignItems: 'stretch',
	},

	'& button[data-testid="country-selector-trigger"]': {
		width: sizing.phoneInputCountryButtonWidth,
		padding: `${spacing.lg}px`,
		borderRight: `1px solid ${colors.border}`,
		borderRadius: `${radius.badge}px 0 0 ${radius.badge}px`,
	},

	// Right box: phone number input.
	// Scoped to the library's class — the country dropdown's search input
	// (CountrySearchSelect) lives inside this container and must not inherit these.
	'& .PhoneInputInput': {
		flex: '1 0 0',
		borderRadius: `0 ${radius.badge}px ${radius.badge}px 0`,
		border: `1px solid ${colors.border}`,
		backgroundColor: colors.surfaceWhite,
		padding: `${spacing.lg}px`,
		color: colors.textSecondary,
		fontFamily: typography.fontFamily,
		fontSize: `${typography.fontSizeLg}px`,
		fontWeight: typography.fontWeightRegular,
		lineHeight: typography.lineHeightLg,
		letterSpacing: typography.letterSpacingWide,
		outline: 'none',
		boxSizing: 'border-box',

		'&::placeholder': {
			color: colors.textSecondary,
			fontStyle: 'normal',
		},

		'&:focus-visible': {
			...focusVisible,
			outlineColor: colors.focusBorder,
			outlineOffset: '-3px',
		},

		[theme.breakpoints.down('md')]: {
			borderRadius: `0 ${radius.badge}px ${radius.badge}px 0`,
			border: `1px solid ${colors.border}`,
			padding: `${spacing.sm}px ${spacing.lg}px`,
			minHeight: sizing.mobilePhoneInputHeight,
			minWidth: sizing.mobilePhoneInputWidth,
		},
	},
}))

export const StyledErrorBox = styled(Box)(({ theme }) => ({
	padding: theme.spacing(1.5, 2),
	borderRadius: theme.shape.borderRadius,
	backgroundColor: colors.errorBg,
	border: `1px solid ${colors.errorBorder}`,
	width: '100%',
}))

export const StyledErrorText = styled(Box)(({ theme }) => ({
	...theme.typography.caption,
	color: theme.palette.error.main,
	margin: 0,
}))

export const StyledButton = styled('button')(({ theme }) => ({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: theme.spacing(1),
	width: '100%',
	height: sizing.signInVerifyButtonHeight,
	border: 'none',
	cursor: 'pointer',
	fontFamily: typography.fontFamily,
	color: colors.textInverse,
	borderRadius: theme.spacing(8),
	padding: theme.spacing(1, 2),
	textTransform: 'uppercase',
	letterSpacing: typography.letterSpacingWide,
	fontWeight: typography.fontWeightBold,
	fontSize: `${typography.fontSizeMd}px`,
	lineHeight: typography.lineHeightLg,
	backgroundColor: colors.brand,
	'&:hover': {
		backgroundColor: colors.brandHover,
	},
	'&:focus-visible': {
		...focusVisible,
		outlineColor: colors.brand,
	},
	'&:disabled': {
		backgroundColor: colors.textDisabled,
		color: colors.textDisabledOnDark,
		cursor: 'default',
	},
}))

export const StyledHeaderBox = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	padding: theme.spacing(0, 3),
	borderBottom: `1px solid ${theme.palette.divider}`,
	height: sizing.signInModalHeaderHeight,
	alignSelf: 'stretch',
	[theme.breakpoints.down('md')]: {
		// The in-flow DrawerPuller above this header already contributes 24px (8px margin +
		// 8px pill + 8px margin), so only 12px more is needed here to reach the design's
		// ~36px above the title — a full 36px top padding doubles the gap.
		padding: `${spacing.md}px ${spacing.lg}px ${sizing.mobileHeaderPaddingBottom}px`,
		minHeight: sizing.mobileHeaderMinHeight,
		borderBottom: `1px solid ${theme.palette.divider}`,
		borderLeft: 'none',
		borderRight: 'none',
		borderTop: 'none',
	},
}))

export const StyledTitle = styled(Box)({
	color: colors.textPrimary,
	fontFamily: typography.fontFamily,
	fontSize: `${typography.fontSizeLg}px`,
	fontWeight: typography.fontWeightBold,
	lineHeight: typography.lineHeightLg,
	letterSpacing: typography.letterSpacingWide,
	flex: 1,
})

export const StyledCloseButton = styled(IconButton)(({ theme }) => ({
	padding: theme.spacing(1),
	'&:hover': {
		backgroundColor: colors.overlaySubtle,
	},
	'&:focus-visible': {
		...focusVisible,
		outlineColor: colors.brand,
	},
}))

export const StyledFooterBox = styled(Box)(({ theme }) => ({
	display: 'flex',
	padding: `${spacing.lg}px ${spacing.xl}px`,
	justifyContent: 'space-between',
	alignItems: 'center',
	alignSelf: 'stretch',
	borderTop: `1px solid ${colors.divider}`,
	[theme.breakpoints.down('md')]: {
		padding: `${spacing.lg}px`,
		minHeight: sizing.mobileFooterMinHeight,
		borderTop: `1px solid ${colors.divider}`,
		borderRight: 'none',
		borderBottom: 'none',
		borderLeft: 'none',
	},
}))

export const StyledButtonContainer = styled(Box)({
	flex: 1,
	display: 'flex',
	alignItems: 'stretch',
})

export const StyledHelperLinksBox = styled(Box)({
	display: 'flex',
	flexDirection: 'column',
	gap: `${spacing.lg}px`,
	textAlign: 'center',
	width: '100%',
})

export const StyledHelperText = styled(Box)(({ theme }) => ({
	fontSize: typography.fontSizeLg,
	lineHeight: typography.lineHeightLg,
	letterSpacing: typography.letterSpacingNormal,
	color: theme.palette.text.primary,
	'& a': {
		color: theme.palette.text.primary,
		textDecoration: 'underline',
		fontWeight: typography.fontWeightBold,
		cursor: 'pointer',
		'&:hover': {
			color: colors.brand,
		},
	},
}))

export const StyledVerifyTitle = styled(Box)({
	color: colors.textPrimary,
	fontFamily: typography.fontFamily,
	fontSize: `${typography.fontSizeXl}px`,
	fontWeight: typography.fontWeightBold,
	lineHeight: typography.lineHeightXl,
	letterSpacing: typography.letterSpacingNormalLg,
	textAlign: 'left',
	marginBottom: `${spacing.xxl}px`,
	wordBreak: 'break-word',
	overflowWrap: 'break-word',
	maxWidth: '100%',
	display: 'inline-block',
})

export const StyledVerifyText = styled(Box)({
	color: colors.textPrimary,
	fontFamily: typography.fontFamily,
	fontSize: `${typography.fontSizeLg}px`,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightLg,
	letterSpacing: typography.letterSpacingWide,
	textAlign: 'center',
	marginBottom: `${spacing.lg}px`,
})

export const StyledOtpInputContainer = styled(Box)({
	display: 'flex',
	gap: `${spacing.sm}px`,
	justifyContent: 'center',
	marginBottom: `${spacing.lg}px`,
	width: '100%',
})

export const StyledOtpInput = styled('input')(({ theme }) => ({
	width: sizing.otpInputWidth,
	height: sizing.otpInputHeight,
	padding: `${spacing.lg}px`,
	boxSizing: 'border-box',
	borderRadius: `${radius.badge}px`,
	border: `1px solid ${colors.border}`,
	backgroundColor: colors.surfaceWhite,
	color: colors.textPrimary,
	fontFamily: typography.fontFamily,
	fontSize: `${typography.fontSizeLg}px`,
	fontWeight: typography.fontWeightBold,
	lineHeight: typography.lineHeightLg,
	textAlign: 'center',
	cursor: 'pointer',
	outline: 'none',
	transition: 'all 0.2s ease',

	'&:focus-visible': {
		...focusVisible,
		outlineColor: colors.brand,
		borderColor: colors.focusBorder,
		boxShadow: `0 0 0 2px ${colors.overlayMedium}`,
	},

	[theme.breakpoints.down('md')]: {
		border: `1px solid ${colors.border}`,
		padding: `${spacing.lg}px`,
		width: sizing.mobileOtpInputWidth,
		height: sizing.mobileOtpInputHeight,
	},

	'&:disabled': {
		backgroundColor: colors.overlaySubtle,
		cursor: 'not-allowed',
	},

	'&[aria-invalid="true"]': {
		borderColor: colors.errorBorder,
		backgroundColor: colors.errorBg,
	},
}))

export const StyledResultIcon = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'isSuccess',
})<{ isSuccess: boolean }>({
	display: 'flex',
	justifyContent: 'center',
	marginBottom: `${spacing.lg}px`,
})

export const StyledResultTitle = styled(Box)({
	color: colors.textPrimary,
	fontFamily: typography.fontFamily,
	fontSize: `${typography.fontSizeXl}px`,
	fontWeight: typography.fontWeightBold,
	lineHeight: typography.lineHeightXl,
	letterSpacing: typography.letterSpacingNormalLg,
	textAlign: 'center',
	marginBottom: `${spacing.sm}px`,
})

export const StyledResultMessage = styled(Box)({
	color: colors.textSecondary,
	fontFamily: typography.fontFamily,
	fontSize: `${typography.fontSizeMd}px`,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingWide,
	textAlign: 'center',
})

export const StyledEmailHelperText = styled(Box)({
	color: colors.textPrimary,
	fontFamily: typography.fontFamily,
	fontSize: `${typography.fontSizeMd}px`,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingWide,
	textAlign: 'left',
	width: '100%',
})

// Email field — stacked label-over-value inside a bordered box (per Figma)
export const StyledEmailFieldBox = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'hasError',
})<{ hasError?: boolean }>(({ hasError, theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'stretch',
	width: '100%',
	boxSizing: 'border-box',
	borderRadius: `${radius.badge}px`,
	border: `1px solid ${hasError ? colors.errorBorder : colors.border}`,
	backgroundColor: colors.surfaceWhite,
	padding: `${spacing.lg}px`,

	'&:focus-within': {
		borderColor: hasError ? colors.errorBorder : colors.focusBorder,
	},

	[theme.breakpoints.down('md')]: {
		borderRadius: `${radius.badge}px`,
		border: `1px solid ${hasError ? colors.errorBorder : colors.border}`,
		padding: `${spacing.sm}px ${spacing.lg}px`,
		minHeight: sizing.mobileEmailFieldHeight,
		minWidth: sizing.mobileEmailFieldWidth,
		justifyContent: 'center',
	},
}))

export const StyledEmailLabel = styled('label')({
	color: colors.textSecondary,
	fontFamily: typography.fontFamily,
	fontSize: `${typography.fontSizeMd}px`,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingWide,
	cursor: 'pointer',
})

export const StyledEmailInput = styled('input')({
	width: '100%',
	border: 'none',
	outline: 'none',
	background: 'transparent',
	padding: 0,
	margin: 0,
	color: colors.textPrimary,
	fontFamily: typography.fontFamily,
	fontSize: `${typography.fontSizeLg}px`,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingWide,

	'&::placeholder': {
		color: colors.textDisabled,
		opacity: 1,
	},

	// Focus is indicated by the wrapping StyledEmailFieldBox border (:focus-within) —
	// an outline here would draw a second border inside the box.
	'&:focus-visible': {
		outline: 'none',
	},

	'&:disabled': {
		cursor: 'not-allowed',
	},
})
